import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import React, { FC, ReactElement, useEffect } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { nodeApi, phpApi } from '../../api';
import { api, setAuthTokenTyped } from '../../api/api';
import { setAuthToken } from '../../api/default';
import { refreshAccessToken } from '../../api/requests';
import { LocalStorageKey } from '../../enums';
import { useAuth, useLocalStorage } from '../../hooks';
import { RefreshAccessTokenResponse, UseAuth } from '../../interfaces';
import { FrontendApiError } from '../../models';
import { UseLocalStorage } from '../../types/internal';

import { AxiosInterceptorProps } from './axiosInterceptor.types';

export const AxiosInterceptor: FC<AxiosInterceptorProps> = (props: AxiosInterceptorProps): ReactElement => {
  const { t }: TransProps<never> = useTranslation();
  const { children }: AxiosInterceptorProps = props;
  const { signOutCleanup }: UseAuth = useAuth();
  const [, storeAuthorizationToken]: UseLocalStorage<string | null> = useLocalStorage<string | null>(
    LocalStorageKey.AuthorizationToken, null
  );
  const queryClient: QueryClient = useQueryClient();

  useEffect(
    (): (() => void) => {
      const responseInterceptor: (response: AxiosResponse) => AxiosResponse = (response: AxiosResponse): AxiosResponse => response;

      const refreshAccessTokenAction: (error: AxiosError<Error> | FrontendApiError) => void | Promise<void> = (
        error: AxiosError<Error> | FrontendApiError
      ): void | Promise<void> => {
        try {
          refreshAccessToken()
            .then((response: RefreshAccessTokenResponse): void => {
              setAuthTokenTyped(response.data.token);
              setAuthToken(response.data.token);
              storeAuthorizationToken(response.data.token);
              void queryClient.invalidateQueries();
            })
            .catch((): void => {
              signOutCleanup(true);
              storeAuthorizationToken(null);
            });
        } catch {
          throw error;
        }
      };

      const errorInterceptor: (error: AxiosError<Error> | FrontendApiError) => Promise<void> = async (
        error: AxiosError<Error> | FrontendApiError,
      ): Promise<void> => {
        if (error instanceof FrontendApiError) {
          if (error.requestHasAuthorization
            && error?.responseCode === 401
            && (
              !error.endpointUrl
              || !['/api/user/refresh', '/api/user/extend-session'].includes(error.endpointUrl)
            )
          ) {
            void refreshAccessTokenAction(error);
          } else {
            throw error;
          }
        } else if ('response' in error) {
          const requestHasAuthorization: boolean = !!error.config?.headers['X-AUTH-TOKEN'];
          if (requestHasAuthorization
            && error.response?.status === 401
            && (
              !error.config?.url
              || !['/api/user/refresh', '/api/user/extend-session'].includes(error.config?.url)
            )
          ) {
            void refreshAccessTokenAction(error);
          } else if (requestHasAuthorization && error.response?.status === 401 && error.config?.url === '/api/user/refresh') {
            signOutCleanup(true);
            storeAuthorizationToken(null);
          } else {
            throw error;
          }
        } else if (error.request && !error.code) {
          throw new Error(
            t('competence.app.errors.custom.noInternetConnection'),
          );
        } else {
          throw new Error(
            t('competence.app.errors.custom.unknownApiError'),
          );
        }
      };

      const interceptorsIds: number[] = [];
      interceptorsIds.push(api.interceptors.response.use(responseInterceptor, errorInterceptor));
      interceptorsIds.push(phpApi.interceptors.response.use(responseInterceptor, errorInterceptor));
      interceptorsIds.push(nodeApi.interceptors.response.use(responseInterceptor, errorInterceptor));

      return (): void => {
        api.interceptors.response.eject(interceptorsIds[0]);
        phpApi.interceptors.response.eject(interceptorsIds[1]);
        nodeApi.interceptors.response.eject(interceptorsIds[2]);
      };
    },
    [queryClient, signOutCleanup, storeAuthorizationToken, t],
  );

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
